/**
 * Typed error model. ApiError is thrown for every non-2xx response from
 * saas-auth. Callers switch on `code`.
 *
 * The union grows as endpoints surface new codes. Unknown backend codes map
 * to `UNKNOWN` with the original string preserved in `details.raw`.
 */

export type ErrorCode =
  // Auth round-trip / session
  | 'INVALID_RETURN_URL'
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'INVALID_CREDENTIALS'
  // Tenant / Product / Brand
  | 'PRODUCT_NOT_FOUND'
  | 'PRODUCT_SLUG_TAKEN'
  | 'JWT_AUDIENCE_TAKEN'
  | 'RESERVED_JWT_AUDIENCE'
  | 'CANONICAL_AUTH_HOSTNAME_TAKEN'
  | 'CANONICAL_LOGIN_HOSTNAME_TAKEN'
  | 'PRODUCT_IN_USE'
  // Product app portfolio (spec 0031)
  | 'INVALID_APP_DOCROOT'
  | 'APP_HAS_HOSTNAMES'
  | 'APP_SLUG_TAKEN'
  | 'RESERVED_APP_SLUG'
  | 'PRIMARY_APP_MUST_BE_FRONTEND'
  // App portfolio roles (SASADM-0116; saas-auth SASATH-0234/0236/0237/0238/0239).
  | 'APP_ROLE_SINGLETON_CONFLICT'
  | 'PRIMARY_APP_MUST_BE_PUBLIC'
  | 'APP_ROLE_IMMUTABLE'
  | 'APP_ROLE_CHANGE_BLOCKED'
  | 'PRIMARY_CONFLICT'
  // SASADM-0133 (saas-auth SASATH-0285) — a SELF_CONTAINED app is exclusive:
  // 409 both ways (adding one when apps exist, adding any app when one exists).
  // The greyed-out Add controls are the guard; this code is the backstop.
  | 'APP_SELF_CONTAINED_EXCLUSIVE'
  // SASADM-0152 (saas-auth SASATH-0336) — the RESELLER_ADMIN app requires the product's
  // PRODUCT_ADMIN app (409 on create without it), and the PRODUCT_ADMIN app can't be deleted
  // while a RESELLER_ADMIN app exists (409; no screen deletes a Product Admin app today, so
  // that one is mapped copy only).
  | 'RESELLER_CONSOLE_REQUIRES_PRODUCT_CONSOLE'
  | 'PRODUCT_CONSOLE_REQUIRED_BY_RESELLER_CONSOLE'
  // App scope TENANT vs PRODUCT (SASADM-0104; saas-auth SASATH-0208) — a
  // product-admin app must be a front-end. SASADM-0110: scope is immutable on
  // edit in this UI, so the backend's scope-change rejection is a backstop only.
  | 'PRODUCT_SCOPE_REQUIRES_FRONTEND'
  | 'APP_SCOPE_CHANGE_BLOCKED'
  // App kind / back-end upstream (spec 0043; saas-auth 0049). SASADM-0066 adds the
  // SELF_CONTAINED analogue of BACKEND_APP_HAS_DOCROOT (saas-auth 0092).
  | 'BACKEND_APP_HAS_DOCROOT'
  | 'SELF_CONTAINED_APP_HAS_DOCROOT'
  | 'FRONTEND_APP_HAS_UPSTREAM'
  | 'UPSTREAM_REQUIRED'
  | 'INVALID_UPSTREAM'
  | 'UPSTREAM_NOT_ALLOWED'
  | 'APP_KIND_CHANGE_BLOCKED'
  // Hostname → app assignment (spec 0032)
  | 'APP_PRODUCT_MISMATCH'
  | 'APP_ON_NON_APP_HOSTNAME'
  | 'APP_NOT_FOUND'
  // Routing deploy (spec 0042; saas-auth 0046)
  | 'DEPLOY_DISPATCH_FAILED'
  | 'DEPLOY_NOT_CONFIGURED'
  // In-app Caddy deploy (spec 0064; saas-auth SASATH-0093) — synchronous 500s on
  // a bad render/reload; the last-good config keeps serving.
  | 'CADDY_CONFIG_INVALID'
  | 'CADDY_RELOAD_FAILED'
  // SASADM-0176 (saas-auth SASATH-0392) — 409 on a synchronous deploy: a held-back fragment's
  // previous routing still claims a host that now belongs to someone else (`error.details`).
  | 'CADDY_HELD_BACK_HOST_CONFLICT'
  // SASADM-0176 (saas-auth SASATH-0391/0392) — 409 on removing a hostname (or changing / removing
  // the white-label domain, or deleting an app) another hostname redirects to;
  // `error.details.hostnameIds` = the redirect sources.
  | 'HOSTNAME_IS_REDIRECT_TARGET'
  // SASADM-0176 (saas-auth SASATH-0393 §1) — 409 on deleting the product's primary app.
  | 'APP_IS_PRIMARY'
  // Product DNS targets (spec 0033)
  | 'INVALID_DNS_TARGET'
  | 'BRAND_NOT_FOUND'
  | 'BRAND_NOT_CONFIGURED'
  | 'BRAND_PRODUCT_MISMATCH'
  | 'BRAND_MUST_BE_CLEARED_FIRST'
  | 'BRAND_IN_USE'
  | 'BRAND_IS_PRODUCT_DEFAULT'
  | 'BRAND_SLUG_TAKEN'
  | 'INVALID_AUTH_PROVIDER'
  | 'INVALID_TEMPLATE'
  | 'INVALID_GUEST_SIGNUP_URL'
  | 'SAAS_ADMIN_PRODUCT_IMMUTABLE'
  // Brand theme editor (SASADM-0154; saas-auth SASATH-0346) — 409 stale etag,
  // 422 save-time contrast (`error.failures` in `details.raw`), 400 bad input.
  | 'THEME_CHANGED'
  | 'THEME_CONTRAST_FAILED'
  | 'VALIDATION_FAILED'
  | 'INVALID_JSON'
  // Brand asset upload (saas_auth spec 0017) + URL model (0023/0024)
  | 'INVALID_IMAGE'
  | 'UNSUPPORTED_IMAGE_TYPE'
  | 'IMAGE_TOO_LARGE'
  | 'IMAGE_DIMENSIONS_TOO_LARGE'
  | 'INVALID_ASSET_BASE_URL'
  // Brand image slots (SASADM-0156; saas-auth SASATH-0347, #962 §3.5) — 400s whose
  // server `message` is shown as-is (expected vs actual, the rejection reason).
  // SASADM-0160: `SVG_REJECTED` also carries `error.reason`, and over 5 MB is
  // 413 ASSET_TOO_LARGE; a code-less 413 (an edge page) is still keyed off the status.
  | 'SVG_REJECTED'
  | 'ASSET_DIMENSIONS'
  | 'ASSET_TYPE'
  | 'ASSET_TOO_LARGE'
  // Brand theme bundle (SASADM-0159; saas-auth SASATH-0351 §3.2) — 413 a zip over
  // 10 MB; 400 an apply with any container/file error (`error.errors[]` in
  // `details.raw`, the §3.3 report row shape).
  | 'BUNDLE_TOO_LARGE'
  | 'BUNDLE_INVALID'
  // Hostname
  | 'INVALID_HOSTNAME'
  | 'HOSTNAME_ALREADY_REGISTERED'
  | 'PRIMARY_HOSTNAME_DELETE_BLOCKED'
  | 'HOSTNAME_NOT_DNS_VERIFIED'
  // SASADM-0136 (saas-auth SASATH-0286 U2) — a hostname type contradicts the
  // product's authMode/appTopology posture (e.g. LOGIN for an EXTERNAL product).
  | 'HOSTNAME_TYPE_CONTRADICTS_PRODUCT_POSTURE'
  // Entra redirect host (SASADM-0164; saas-auth SASATH-0379)
  | 'WHITELABEL_AUTH_HOST_NOT_VERIFIED'
  | 'WHITELABEL_IN_USE_BY_MICROSOFT_SSO'
  // Tenant identity console (SASADM-0169; saas-auth SASATH-0376/0382)
  | 'AUTH_HOST_IN_USE_BY_MICROSOFT_SSO'
  | 'LAST_OWNER_PROTECTED'
  | 'MEMBER_ID_AMBIGUOUS'
  | 'ROLE_CHANGE_CROSSES_TABLES'
  | 'INVALID_MEMBER_KIND'
  | 'STATUS_NOT_ALLOWED'
  // White-label domain (SASADM-0165; saas-auth SASATH-0297)
  | 'INVALID_WHITELABEL_DOMAIN'
  | 'WHITELABEL_HOSTS_REQUIRED'
  | 'INVALID_WHITELABEL_PREFIX'
  | 'WHITELABEL_DUPLICATE_PREFIX'
  | 'WHITELABEL_HOSTNAME_TYPE_UNSUPPORTED'
  | 'WHITELABEL_DOMAIN_RESERVED'
  // Member / User
  | 'USER_NOT_FOUND'
  | 'TENANT_NOT_FOUND'
  | 'TENANT_INACTIVE'
  | 'MEMBER_NOT_FOUND'
  // Tenant invitations (saas_auth spec 0011)
  | 'ALREADY_ACTIVE_MEMBER'
  | 'INVITATION_ALREADY_PENDING'
  | 'INVITATION_ALREADY_ACCEPTED'
  // SASADM-0170 (saas-auth SASATH-0387 §3A) — customer invitations are refused while the
  // tenant's public app is turned off.
  | 'PUBLIC_APP_DISABLED'
  // SASADM-0171 (saas-auth SASATH-0389 §4.1) — 409 on a product-app role change away from PUBLIC
  // or a tenant product reassignment while tenants hold an override; the body lists the tenant ids.
  | 'APP_HAS_TENANT_OVERRIDES'
  // SASADM-0172 (saas-auth SASATH-0367) — another app already holds affiliate.<tenant> hosts.
  | 'AFFILIATE_HOST_CONFLICT'
  // SASADM-0172 (saas-auth SASATH-0388 §3C.2) — 409 on app create / docroot change when the folder
  // equals, sits under or above a tenant's custom build or offline page; the message names the ids.
  | 'DOCROOT_IN_USE_BY_TENANT_OVERRIDE'
  // OIDC
  | 'OIDC_PROVIDER_DISABLED'
  | 'CANNOT_REMOVE_LAST_TENANT'
  // Product → OIDC-clients reverse view (spec 0062; saas-auth SASATH-0079).
  // 409 when detaching a PLATFORM-scoped client from a product — the client
  // covers all products globally, so it's removed only by changing the client's
  // own access scope. The UI prevents it (honors `removableFromProduct:false`)
  // and handles this code defensively if it still occurs.
  | 'CLIENT_IS_PLATFORM_SCOPED'
  // OIDC per-client member access (spec 0058; saas-auth SASATH-0076). Field-level
  // 400 on the invitation OIDC-client multi-select — a chosen client no longer
  // allow-lists this tenant (pre-filtered picker should prevent it).
  | 'OIDC_CLIENT_NOT_ALLOWED_FOR_TENANT'
  // OIDC create/edit two-mode contract (spec 0057; saas-auth SASATH-0073/0075).
  // Field-level 400 codes surfaced on the relevant wizard field.
  | 'LOGIN_TENANT_NOT_ALLOWED'
  | 'LOGIN_HOSTNAME_REQUIRED'
  | 'ALLOWED_TENANTS_REQUIRED'
  | 'REDIRECT_URI_INVALID'
  | 'BRAND_HOST_REQUIRED'
  // Product agent clients (SASADM-0149; saas-auth SASATH-0331). Field-level 400s
  // on create / redirect-URI / scope subset. Distinct from OIDC's
  // REDIRECT_URI_INVALID / TENANT_INACTIVE — the agent API ships these names.
  | 'INVALID_CLIENT_ID'
  | 'CLIENT_ID_TAKEN'
  | 'INVALID_REDIRECT_URI'
  | 'INVALID_SCOPE'
  | 'TENANT_NOT_IN_PRODUCT'
  | 'TENANT_NOT_ACTIVE'
  // Captcha automation (SASADM-0086; saas-auth SASATH-0151)
  | 'TOO_MANY_HOSTNAMES'
  | 'NO_HOSTNAMES'
  | 'WIDGET_NOT_PROVISIONED'
  // Cross-cutting
  | 'TENANT_ORIGIN_MISMATCH'
  | 'RATE_LIMIT_REJECTED'
  // SASADM-0154 — the 429 code of the theme preview/save rate limits.
  | 'RATE_LIMITED'
  | 'INVALID_LOCALE'
  // Server-side list sort (SASADM-0111; saas-auth SASATH-0212) — 400 when a
  // `sort=<field>,<dir>` names a field outside the server's allowlist. The UI
  // only offers allowlisted columns, so this is a backstop.
  | 'INVALID_SORT'
  // SASADM-0158 — a 502/503/504 whose body isn't JSON (an edge or proxy page).
  | 'SERVICE_UNAVAILABLE'
  // UI-synthesized
  | 'RESERVED_COPY_KEY'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface BackendErrorEnvelope {
  success: false;
  error: {
    code: string;
    message?: string;
  };
}

export interface ApiErrorDetails {
  message?: string;
  raw?: unknown;
  /** SASADM-0154 — a 429's `Retry-After` in seconds, when the header parses. */
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  public readonly code: ErrorCode;
  public readonly status: number;
  public readonly requestId: string | undefined;
  public readonly details: ApiErrorDetails;

  constructor(
    code: ErrorCode,
    status: number,
    options: { requestId?: string | undefined; details?: ApiErrorDetails } = {},
  ) {
    super(`API error: ${code}`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = options.requestId;
    this.details = options.details ?? {};
  }
}

// Exported so the copy catalog can be checked against it (SASADM-0113 C7):
// every shipped code must have an `errors.codes.<CODE>` string, or the UI
// renders the raw code. Read-only — `mapBackendCode` is the only consumer.
export const SHIPPED_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'INVALID_RETURN_URL',
  'CSRF_VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'PRODUCT_NOT_FOUND',
  'PRODUCT_SLUG_TAKEN',
  'JWT_AUDIENCE_TAKEN',
  'RESERVED_JWT_AUDIENCE',
  'CANONICAL_AUTH_HOSTNAME_TAKEN',
  'CANONICAL_LOGIN_HOSTNAME_TAKEN',
  'PRODUCT_IN_USE',
  'INVALID_APP_DOCROOT',
  'APP_HAS_HOSTNAMES',
  'APP_SLUG_TAKEN',
  'RESERVED_APP_SLUG',
  'PRIMARY_APP_MUST_BE_FRONTEND',
  'APP_ROLE_SINGLETON_CONFLICT',
  'PRIMARY_APP_MUST_BE_PUBLIC',
  'APP_ROLE_IMMUTABLE',
  'APP_ROLE_CHANGE_BLOCKED',
  'PRIMARY_CONFLICT',
  'APP_SELF_CONTAINED_EXCLUSIVE',
  'RESELLER_CONSOLE_REQUIRES_PRODUCT_CONSOLE',
  'PRODUCT_CONSOLE_REQUIRED_BY_RESELLER_CONSOLE',
  'PRODUCT_SCOPE_REQUIRES_FRONTEND',
  'APP_SCOPE_CHANGE_BLOCKED',
  'BACKEND_APP_HAS_DOCROOT',
  'SELF_CONTAINED_APP_HAS_DOCROOT',
  'FRONTEND_APP_HAS_UPSTREAM',
  'UPSTREAM_REQUIRED',
  'INVALID_UPSTREAM',
  'UPSTREAM_NOT_ALLOWED',
  'APP_KIND_CHANGE_BLOCKED',
  'APP_PRODUCT_MISMATCH',
  'APP_ON_NON_APP_HOSTNAME',
  'APP_NOT_FOUND',
  'DEPLOY_DISPATCH_FAILED',
  'DEPLOY_NOT_CONFIGURED',
  'CADDY_CONFIG_INVALID',
  'CADDY_RELOAD_FAILED',
  'CADDY_HELD_BACK_HOST_CONFLICT',
  'HOSTNAME_IS_REDIRECT_TARGET',
  'APP_IS_PRIMARY',
  'INVALID_DNS_TARGET',
  'BRAND_NOT_FOUND',
  'BRAND_NOT_CONFIGURED',
  'BRAND_PRODUCT_MISMATCH',
  'BRAND_MUST_BE_CLEARED_FIRST',
  'BRAND_IN_USE',
  'BRAND_IS_PRODUCT_DEFAULT',
  'BRAND_SLUG_TAKEN',
  'INVALID_AUTH_PROVIDER',
  'INVALID_TEMPLATE',
  'INVALID_GUEST_SIGNUP_URL',
  'SAAS_ADMIN_PRODUCT_IMMUTABLE',
  'THEME_CHANGED',
  'THEME_CONTRAST_FAILED',
  'VALIDATION_FAILED',
  'INVALID_JSON',
  'INVALID_IMAGE',
  'UNSUPPORTED_IMAGE_TYPE',
  'IMAGE_TOO_LARGE',
  'IMAGE_DIMENSIONS_TOO_LARGE',
  'INVALID_ASSET_BASE_URL',
  'SVG_REJECTED',
  'ASSET_DIMENSIONS',
  'ASSET_TYPE',
  'ASSET_TOO_LARGE',
  'BUNDLE_TOO_LARGE',
  'BUNDLE_INVALID',
  'INVALID_HOSTNAME',
  'HOSTNAME_ALREADY_REGISTERED',
  'PRIMARY_HOSTNAME_DELETE_BLOCKED',
  'HOSTNAME_NOT_DNS_VERIFIED',
  'HOSTNAME_TYPE_CONTRADICTS_PRODUCT_POSTURE',
  'WHITELABEL_AUTH_HOST_NOT_VERIFIED',
  'WHITELABEL_IN_USE_BY_MICROSOFT_SSO',
  'AUTH_HOST_IN_USE_BY_MICROSOFT_SSO',
  'LAST_OWNER_PROTECTED',
  'MEMBER_ID_AMBIGUOUS',
  'ROLE_CHANGE_CROSSES_TABLES',
  'INVALID_MEMBER_KIND',
  'STATUS_NOT_ALLOWED',
  'INVALID_WHITELABEL_DOMAIN',
  'WHITELABEL_HOSTS_REQUIRED',
  'INVALID_WHITELABEL_PREFIX',
  'WHITELABEL_DUPLICATE_PREFIX',
  'WHITELABEL_HOSTNAME_TYPE_UNSUPPORTED',
  'WHITELABEL_DOMAIN_RESERVED',
  'USER_NOT_FOUND',
  'TENANT_NOT_FOUND',
  'TENANT_INACTIVE',
  'MEMBER_NOT_FOUND',
  'ALREADY_ACTIVE_MEMBER',
  'INVITATION_ALREADY_PENDING',
  'INVITATION_ALREADY_ACCEPTED',
  'PUBLIC_APP_DISABLED',
  'APP_HAS_TENANT_OVERRIDES',
  'AFFILIATE_HOST_CONFLICT',
  'DOCROOT_IN_USE_BY_TENANT_OVERRIDE',
  'OIDC_PROVIDER_DISABLED',
  'CANNOT_REMOVE_LAST_TENANT',
  'CLIENT_IS_PLATFORM_SCOPED',
  'OIDC_CLIENT_NOT_ALLOWED_FOR_TENANT',
  'LOGIN_TENANT_NOT_ALLOWED',
  'LOGIN_HOSTNAME_REQUIRED',
  'ALLOWED_TENANTS_REQUIRED',
  'REDIRECT_URI_INVALID',
  'BRAND_HOST_REQUIRED',
  'INVALID_CLIENT_ID',
  'CLIENT_ID_TAKEN',
  'INVALID_REDIRECT_URI',
  'INVALID_SCOPE',
  'TENANT_NOT_IN_PRODUCT',
  'TENANT_NOT_ACTIVE',
  'TOO_MANY_HOSTNAMES',
  'NO_HOSTNAMES',
  'WIDGET_NOT_PROVISIONED',
  'TENANT_ORIGIN_MISMATCH',
  'RATE_LIMIT_REJECTED',
  'RATE_LIMITED',
  'INVALID_LOCALE',
  'INVALID_SORT',
  'SERVICE_UNAVAILABLE',
]);

export function mapBackendCode(code: string | undefined): ErrorCode {
  if (!code) return 'UNKNOWN';
  if (SHIPPED_CODES.has(code as ErrorCode)) return code as ErrorCode;
  if (code === 'INVALID_CSRF') return 'CSRF_VALIDATION_FAILED';
  if (code === 'INVALID_ORIGIN') return 'TENANT_ORIGIN_MISMATCH';
  return 'UNKNOWN';
}

/**
 * SASATH-0389 §4.1 / SASATH-0388 §3C.2 — the tenant ids a 409 APP_HAS_TENANT_OVERRIDES or
 * DOCROOT_IN_USE_BY_TENANT_OVERRIDE lists, from `error.details.tenantIds`: absent today; saas-auth
 * adds it (backlog 0025 #11). Today the ids are only in `error.message`, which is never parsed.
 * SASADM-0176 (SASATH-0393 §1) — an app delete's 409 APP_HAS_HOSTNAMES lists them the same way.
 */
export function overrideTenantIds(err: ApiError): number[] {
  const e = (
    err.details.raw as {
      error?: { details?: { tenantIds?: unknown } };
    } | null
  )?.error;
  const ids = e?.details?.tenantIds;
  return Array.isArray(ids) ? ids.filter((x): x is number => typeof x === 'number') : [];
}

/**
 * SASADM-0176 (SASATH-0391/0392) — the redirect SOURCE hostname ids a 409
 * HOSTNAME_IS_REDIRECT_TARGET lists, from `error.details.hostnameIds`.
 */
export function redirectSourceIds(err: ApiError): number[] {
  const e = (
    err.details.raw as {
      error?: { details?: { hostnameIds?: unknown } };
    } | null
  )?.error;
  const ids = e?.details?.hostnameIds;
  return Array.isArray(ids) ? ids.filter((x): x is number => typeof x === 'number') : [];
}

export function apiErrorFromEnvelope(status: number, body: unknown, requestId?: string): ApiError {
  const envelope = body as Partial<BackendErrorEnvelope> | null;
  const code = mapBackendCode(envelope?.error?.code);
  const details: ApiErrorDetails = { raw: body };
  if (envelope?.error?.message !== undefined) details.message = envelope.error.message;
  return new ApiError(code, status, { requestId, details });
}
